import { Box, BoxProps } from "@mui/material";
import React from "react";
import { NoPropagateBox } from "../../../commons/NoPropagateBox.js";

interface Props {
  // Read-only label rendered by default. Remains in the DOM with `visibility:
  // hidden` while the editor is open so the cell preserves its measured size
  // (no layout shift between read-only and edit mode).
  displayValue: React.ReactNode;
  // Editor factory. The returned node is mounted in an absolutely-positioned
  // overlay covering the cell. Call `handleBlur` from the editor's own onBlur
  // after committing the value; that exits edit mode and triggers `onEditEnd`.
  renderEditor: (handleBlur: () => void) => React.ReactNode;
  // When true, the displayValue is rendered as plain content with no click
  // affordance or hover outline (acts like the cell is read-only).
  disabled?: boolean;
  // External hook to coordinate mutual exclusion when multiple
  // InlineEditableCell instances share a row (e.g. quantity + unit).
  onEditStart?: () => void;
  onEditEnd?: () => void;
  // When true, the underlying displayValue gets `visibility: hidden` even
  // while not editing. Used by sibling cells to make room for an active
  // overlay (e.g. while quantity is being edited, the unit's displayValue is
  // hidden so the formula icon can use its space).
  hidden?: boolean;
  sx?: BoxProps["sx"];
}

export function InlineEditableCell({
  displayValue,
  renderEditor,
  disabled,
  onEditStart,
  onEditEnd,
  hidden,
  sx,
}: Props) {
  const [isEditing, setIsEditing] = React.useState(false);

  const handleEnter = React.useCallback(() => {
    if (disabled) return;
    setIsEditing(true);
    onEditStart?.();
  }, [disabled, onEditStart]);

  const handleBlur = React.useCallback(() => {
    setIsEditing(false);
    onEditEnd?.();
  }, [onEditEnd]);

  const editorVisible = isEditing && !disabled;
  // The display box is only interactive when it is actually shown: not
  // disabled, not covered by the editor overlay, and not hidden to make room
  // for a sibling's overlay. Otherwise it must leave the tab order and drop
  // its button role / activation handlers so it isn't an invisible focus
  // target for keyboard / assistive-tech users.
  const displayInteractive = !disabled && !editorVisible && !hidden;

  return (
    <NoPropagateBox
      preventDefault={false}
      sx={[
        {
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          ...(editorVisible && { flex: 1 }),
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        onClick={displayInteractive ? handleEnter : undefined}
        // Keyboard activation: the display Box is the click target, so it
        // must also be reachable / activatable by keyboard. `tabIndex={0}`
        // makes it part of the tab order; Enter and Space trigger the
        // same enter-edit-mode path as a mouse click. `role="button"` lets
        // assistive tech announce it as actionable. When not interactive
        // (disabled / editing / hidden) it drops out of the tab order and
        // sheds its button role so it isn't an invisible focus target.
        role={displayInteractive ? "button" : undefined}
        tabIndex={displayInteractive ? 0 : -1}
        onKeyDown={event => {
          if (!displayInteractive) return;
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          handleEnter();
        }}
        sx={{
          paddingX: 0.25,
          marginX: -0.25,
          borderRadius: 0.5,
          cursor: disabled ? "default" : "text",
          visibility: editorVisible || hidden ? "hidden" : "visible",
          ...(disabled
            ? null
            : {
                "&:hover, &:focus-within": {
                  outline: theme => `${theme.palette.grey[300]} solid 1px`,
                },
              }),
        }}
      >
        {displayValue}
      </Box>
      {editorVisible ? (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            width: "100%",
          }}
        >
          {renderEditor(handleBlur)}
        </Box>
      ) : null}
    </NoPropagateBox>
  );
}
